import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from "react";
import {
    AudioAI,
    AudioClipLine,
    AudioEditorClipSourceKind,
    AudioEditorTrackType,
    SoundLibraryAI,
} from "../types/types";
import { ConvertAudioOptions, audioHasURL, convertAudioToEditor } from "../components/common/AudioEditor/utils";
import { DEFAULT_TARGET_LUFS } from "../components/common/AudioEditor/constants";

type SelectedTrackType = (AudioEditorTrackType & { index: number }) | null;

/**
 * The capsule the editor was opened from, when it was opened from Projects or Drafts
 * rather than from the sidebar. Saving then updates that row instead of adding another
 * one to the project, and its voice ids are carried over to the mix.
 */
export type EditorSessionAudio = {
    audioAIId: string;
    name: string;
    language: string;
    voiceAIId?: string | null;
    voiceCloneId?: string | null;
    voicePromptId?: string | null;
    /** Transcript already stored on that audio: the last one a human validated. */
    transcript: string | null;
    /** Language the sound design was inherited from, null when it is this audio's own. */
    inheritedFromLanguage: string | null;
};

interface AudioEditorContextType {
    selectedAudio: ImportedAudioEditorTrackType | null;
    setSelectedAudio: (audio: ImportedAudioEditorTrackType | null) => void;
    importedAudioList: ImportedAudioEditorTrackType[];
    setConvertedAudioList: (audios: ImportedAudioEditorTrackType[]) => void;
    addToImportedList: (
        audio: AudioAI | SoundLibraryAI,
        options?: AudioEditorClipSourceKind | ConvertAudioOptions,
    ) => ImportedAudioEditorTrackType | null;
    tracks: AudioEditorTrackType[];
    setTracks: (tracks: AudioEditorTrackType[]) => void;
    addTrack: (track: AudioEditorTrackType, index?: number) => void;
    removeTrack: (indexToRemove: number) => void;
    selectedTrack: SelectedTrackType | null;
    setSelectedTrack: (track: AudioEditorTrackType | null, index: number | null) => void;
    addToProjectSettings: AddToProjectSettings;
    setAddToProjectSettings: (settings: AddToProjectSettings) => void;
    sessionAudio: EditorSessionAudio | null;
    setSessionAudio: (audio: EditorSessionAudio | null) => void;
    isProcessing: boolean;
    setIsProcessing: (isProcessing: boolean) => void;
    mode: "split" | "drag" | "fadeIn" | "fadeOut" | null;
    setMode: (mode: "split" | "drag" | "fadeIn" | "fadeOut" | null) => void;
    isPlaying: boolean;
    setIsPlaying: (state: boolean) => void;
    /** When true, export applies EBU R128 loudness normalization. Default: false. */
    normalizeOnExport: boolean;
    setNormalizeOnExport: (enabled: boolean) => void;
    /** Target integrated loudness in LUFS when normalizeOnExport is true. */
    targetLufs: number;
    setTargetLufs: (lufs: number) => void;

    // Registerable actions
    playAudio?: () => void;
    pauseAudio?: () => void;
    stopAudio?: () => void;
    exportAudio?: () => void;
    exportAudioToProject?: () => void;
    setZoomLevel?: (level: number) => void;
    zoomIn?: () => void;
    zoomOut?: () => void;
    trim?: () => void;
    /**
     * Loads a clip into the mixer. Resolves once the audio is decoded and placed, with
     * the id actually assigned to the new track — which is not the caller's, since the
     * same asset can be laid down several times.
     */
    newTrack?: (track: AudioEditorTrackType, index?: number) => void | Promise<string | undefined>;
    deleteTrack?: (index: number) => void;
    copyTrack?: (index: number) => void;
    setTrackVolume?: (volume: number) => void;
    getTrackVolume?: (index: number) => number;
    setTrackSpeed?: (speed: number) => void;
    processTrackSpeed?: (trackIndex: number, speed: number, uploadToS3?: boolean) => Promise<void>;
    setActiveTrack?: (index: number) => void;
    removeFades?: (index: number) => void;

    // Action registration
    registerActions: (actions: Partial<Omit<AudioEditorContextType, "registerActions">>) => void;
}

const AudioEditorContext = createContext<AudioEditorContextType | undefined>(undefined);

interface AudioEditorProviderProps {
    children: ReactNode;
}

type AddToProjectSettings = {
    name: string;
    language: string;
    projectId: string;
    /** Set when re-saving onto the capsule the editor was opened from. */
    targetAudioAIId?: string | null;
    /** What the user validated, keyed by clip id. The single input of a save. */
    linesByClip?: Record<string, AudioClipLine[]>;
} | null;

export type ImportedAudioEditorTrackType = AudioEditorTrackType & { editorUID: string };

export const AudioEditorProvider: React.FC<AudioEditorProviderProps> = ({ children }) => {
    const [selectedAudio, setSelectedAudio] = useState<ImportedAudioEditorTrackType | null>(null);
    const [importedAudioList, setConvertedAudioList] = useState<ImportedAudioEditorTrackType[]>([]);
    const [tracks, setTracks] = useState<AudioEditorTrackType[]>([]);
    const [selectedTrack, _setSelectedTrack] = useState<SelectedTrackType>(null);
    const [actions, setActions] = useState<Partial<AudioEditorContextType>>({});
    const [addToProjectSettings, setAddToProjectSettings] = useState<AddToProjectSettings>(null);
    const [sessionAudio, setSessionAudio] = useState<EditorSessionAudio | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [normalizeOnExport, setNormalizeOnExport] = useState(false);
    const [targetLufs, setTargetLufs] = useState(DEFAULT_TARGET_LUFS);

    const [mode, setMode] = useState<"split" | "drag" | "fadeIn" | "fadeOut" | null>(null);

    const registerActions = useCallback(
        (actionsToRegister: Partial<AudioEditorContextType>) => {
            setActions((prev) => {
                const isSame = Object.entries(actionsToRegister).every(
                    ([key, fn]) => prev[key as keyof typeof prev] === fn,
                );
                return isSame ? prev : { ...prev, ...actionsToRegister };
            });
        },
        [setActions],
    );

    const addToImportedList = useCallback(
        (audio: AudioAI | SoundLibraryAI, options?: AudioEditorClipSourceKind | ConvertAudioOptions) => {
            if (audioHasURL(audio)) {
                const converted = convertAudioToEditor(audio, options);
                // add the new audio at the beginning of the list
                setConvertedAudioList((prev) => [converted, ...prev]);
                return converted;
            }
            return null;
        },
        [setConvertedAudioList],
    );

    const addTrack = useCallback(
        (track: AudioEditorTrackType, index?: number) => {
            setTracks((prev) => {
                if (index === undefined || index < 0 || index >= prev.length) {
                    return [...prev, track]; // default: append
                } else {
                    const newTracks = [...prev];
                    newTracks.splice(index, 0, track); // insert at given index
                    return newTracks;
                }
            });
        },
        [setTracks],
    );

    const setSelectedTrack = useCallback(
        (track: AudioEditorTrackType | null, index: number | null) => {
            if (track !== null && index !== null) {
                _setSelectedTrack({ ...track, index });
            } else {
                _setSelectedTrack(null);
            }
        },
        [_setSelectedTrack],
    );

    const removeTrack = useCallback(
        (indexToRemove: number) => {
            setTracks((prev) => {
                const updated = prev.filter((_, i) => i !== indexToRemove);

                // Deselect if current selection was removed
                _setSelectedTrack((selected) => {
                    if (selected?.index === indexToRemove) return null;

                    // If a previous track shifted, adjust index
                    if (selected && selected.index > indexToRemove) {
                        return { ...selected, index: selected.index - 1 };
                    }

                    return selected;
                });

                return updated;
            });
        },
        [setTracks, _setSelectedTrack],
    );

    const value = useMemo(
        () => ({
            selectedAudio,
            setSelectedAudio,
            importedAudioList,
            setConvertedAudioList,
            addToImportedList,
            tracks,
            setTracks,
            addTrack,
            removeTrack,
            selectedTrack,
            setSelectedTrack,
            addToProjectSettings,
            setAddToProjectSettings,
            sessionAudio,
            setSessionAudio,
            isProcessing,
            setIsProcessing,
            mode,
            setMode,
            isPlaying,
            setIsPlaying,
            normalizeOnExport,
            setNormalizeOnExport,
            targetLufs,
            setTargetLufs,
            registerActions,
            ...actions,
        }),
        [
            selectedAudio,
            importedAudioList,
            addToImportedList,
            tracks,
            addTrack,
            removeTrack,
            selectedTrack,
            setSelectedTrack,
            addToProjectSettings,
            sessionAudio,
            isProcessing,
            setIsProcessing,
            mode,
            setMode,
            isPlaying,
            setIsPlaying,
            normalizeOnExport,
            setNormalizeOnExport,
            targetLufs,
            setTargetLufs,
            registerActions,
            actions,
        ],
    );

    return <AudioEditorContext.Provider value={value}>{children}</AudioEditorContext.Provider>;
};

export const useAudioEditor = () => {
    const context = useContext(AudioEditorContext);
    if (!context) {
        throw new Error("useAudioEditor must be used within an AudioEditorProvider");
    }
    return context;
};
